import { fetchWithCsrf } from './fetch';
import { checkSessionLost } from './session';

export interface ApiError {
  status: number | 0;
  message: string;
  details?: unknown;
}

export interface ApiResult<T> {
  ok: boolean;
  data?: T | undefined;
  error?: ApiError | undefined;
  response?: Response | undefined;
}

type JsonBody = Record<string, any> | undefined | null;

function toQuery(params?: Record<string, any>): string {
  if (!params) return '';
  const usp = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === null) return;
    usp.append(k, String(v));
  });
  const s = usp.toString();
  return s ? `?${s}` : '';
}

export class ApiClient {
  constructor(private baseUrl = '') {}

  private async request<T>(path: string, init: RequestInit): Promise<ApiResult<T>> {
    let response: Response | undefined;
    try {
      response = await fetchWithCsrf(this.baseUrl + path, init);
      const contentType = response.headers.get('content-type') || '';
      const isJson = contentType.includes('application/json');
      let payload: any = undefined;
      if (isJson) {
        try { payload = await response.clone().json(); } catch {}
      } else if (await checkSessionLost(response, true)) {
        // HTML where JSON was expected: the session is gone (session.ts)
        return {
          ok: false,
          error: { status: 401, message: 'Logged out' },
          response
        };
      }
      if (!response.ok) {
        return {
          ok: false,
          error: {
            status: response.status,
            message: payload?.message || response.statusText || 'Request failed',
            details: payload
          },
          response
        };
      }
  return { ok: true, data: (payload as T) ?? undefined, response };
    } catch (e: any) {
      return {
        ok: false,
  error: { status: 0, message: e?.message || 'Network error', details: e },
  response: response ?? undefined
      };
    }
  }

  get<T>(path: string, params?: Record<string, any>, init?: RequestInit) {
    return this.request<T>(path + toQuery(params), { ...(init || {}), method: 'GET' });
  }
  delete<T>(path: string, params?: Record<string, any>, init?: RequestInit) {
    return this.request<T>(path + toQuery(params), { ...(init || {}), method: 'DELETE' });
  }
  postJson<T>(path: string, body?: JsonBody, init?: RequestInit) {
    return this.request<T>(path, {
      ...(init || {}),
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(init?.headers || {}) },
      body: body ? JSON.stringify(body) : null
    });
  }
  putJson<T>(path: string, body?: JsonBody, init?: RequestInit) {
    return this.request<T>(path, {
      ...(init || {}),
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...(init?.headers || {}) },
      body: body ? JSON.stringify(body) : null
    });
  }
  postForm<T>(path: string, form: FormData, init?: RequestInit) {
    return this.request<T>(path, { ...(init || {}), method: 'POST', body: form });
  }
}

export const apiClient = new ApiClient('');

export interface PaginationMeta { pageCount: number; totalCount?: number }
export function extractPagination(resp?: Response): PaginationMeta {
  if (!resp) return { pageCount: 0, totalCount: 0 };
  const pageCount = parseInt(resp.headers.get('X-Pagination-Page-Count') || '0', 10);
  const totalCount = parseInt(resp.headers.get('X-Pagination-Total-Count') || '0', 10);
  return { pageCount, totalCount };
}
